/**
 * How an Activity row reads.
 *
 * The backend records a mutation by its schema name (`updateJobById`), so a new mutation is on
 * the monitor the day it ships. The names people ask about get a sentence here; everything else
 * is de-camelled, which is legible if plain ("Update job"), so nothing ever shows as a raw
 * identifier and nothing needs adding here for a row to make sense.
 */
const NAMED: Record<string, string> = {
  LOGIN: 'Signed in',
  LOGIN_FAILED: 'Sign-in failed',
  SIGN_UP: 'Signed up',
  PURCHASE: 'Bought a plan',
  SOFT_DELETE: 'Deleted',
  RESTORE: 'Restored',
  HARD_DELETE: 'Deleted permanently',
  createJobApplication: 'Applied to a job',
  createJob: 'Posted a job',
  updateJobById: 'Edited a job',
  updateJobSeekerById: 'Updated a candidate profile',
  updateEmployerById: 'Updated an employer',
  createCompany: 'Added a company',
  updateCompanyById: 'Edited a company',
  updateJobApplicationStatus: 'Moved an application',
  updateJobApplicationById: 'Updated an application',
  createExternalCandidate: 'Added a candidate',
  uploadResume: 'Uploaded a résumé',
  saveJob: 'Saved a job',
  unsaveJob: 'Unsaved a job',
  createPlacementFee: 'Recorded a placement fee',
  updatePlacementFee: 'Edited a placement fee',
  deletePlacementFee: 'Removed a placement fee',
  assignHr: 'Assigned an HR',
  assignJobHr: 'Assigned an HR to a job',
  setEmployerPremium: 'Changed premium status',
};

export const actionLabel = (action: string | null | undefined): string => {
  if (!action) return '';
  if (NAMED[action]) return NAMED[action];
  const words = action
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\s+by\s+id$/, '')
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const ACTOR_TYPES = [
  { value: '', label: 'Everyone' },
  { value: 'jobseeker', label: 'Job seekers' },
  { value: 'employer', label: 'Employers' },
  { value: 'admin', label: 'Staff' },
];

const ROLE_LABELS: Record<string, string> = { HR: 'HR', SUPER_ADMIN: 'Super Admin', MARKETING: 'Marketing' };

/** "Job seeker", "Employer", or "Staff (HR)" when the staff role is known. */
export const actorTypeLabel = (t: string | null | undefined, role?: string | null): string => {
  if (t === 'admin') {
    const r = role ? ROLE_LABELS[role.toUpperCase()] : undefined;
    return r ? `Staff (${r})` : 'Staff';
  }
  return ({ jobseeker: 'Job seeker', employer: 'Employer' } as Record<string, string>)[t ?? ''] ?? 'Unknown';
};

/** Where a person's record lives in the Control Center, or null if they have none. */
export const actorPath = (type: string | null | undefined, id: string | null | undefined): string | null => {
  if (!id) return null;
  if (type === 'jobseeker') return `/jobseekers/${id}`;
  if (type === 'employer') return `/employers/${id}`;
  if (type === 'admin') return `/user-rbac/${id}`;
  return null;
};

/** Where the thing acted on lives, when it is one of the record types the Control Center opens. */
export const entityPath = (type: string | null | undefined, id: string | null | undefined): string | null => {
  if (!id) return null;
  switch (type) {
    case 'Job': return `/jobs/${id}`;
    case 'JobSeeker': return `/jobseekers/${id}`;
    case 'Employer': return `/employers/${id}`;
    case 'Company': return `/companies/${id}`;
    case 'Application': return `/job-applications/${id}`;
    default: return null;
  }
};

/** Why a Control Center sign-in was refused, as the backend stores it (LOGIN_FAILED rows). */
const SIGN_IN_REASONS: Record<string, string> = {
  unknown_email: 'No such account',
  bad_password: 'Wrong password',
  inactive: 'Inactive account',
  locked: 'Locked out',
  rate_limited: 'Too many attempts',
};

export const signInReasonLabel = (reason: string | null | undefined): string =>
  reason ? (SIGN_IN_REASONS[reason] ?? reason.replace(/_/g, ' ')) : '';
